/**
 * V2 Category — the set/stats maths. Split out of category.js on 2026-07-25
 * (998 lines, plan budget ~400).
 *
 * Every function here is pure: no gateway, no DOM, no imports at all. Several
 * are the executable spec that a Postgres RPC mirrors (computeLeaderboard vs
 * get_category_leaderboard) — keeping them in one dependency-free file makes
 * that role obvious. category.js re-exports all of them.
 */

/** A category id plus all its descendants (client walk, catalog is local). */
export function subtreeIds(rootId, allCats) {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of allCats) {
      if (c.parent_id && ids.has(c.parent_id) && !ids.has(c.id)) {
        ids.add(c.id);
        grew = true;
      }
    }
  }
  return ids;
}

/**
 * V1's header stats (category-panel.js:446-468, 959-967): total yoinks,
 * distinct locations worldwide, and locations whose FIRST yoink landed
 * within the last 7 days. Exported for tests.
 */
export function computeCategoryStats(rows, now = new Date('2100-01-01')) {
  const firstByLocation = new Map();
  for (const r of rows) {
    if (!r.location_id) continue;
    const t = new Date(r.created_at).getTime();
    const prev = firstByLocation.get(r.location_id);
    if (prev === undefined || t < prev) firstByLocation.set(r.location_id, t);
  }
  const weekAgo = now.getTime() - 7 * 86400000;
  let fresh = 0;
  for (const t of firstByLocation.values()) if (t >= weekAgo) fresh += 1;
  return { yoinks: rows.length, locations: firstByLocation.size, newThisWeek: fresh };
}

/** Great-circle distance in km (V1 renderUncollectedNearby's Haversine). */
export function haversineKm(a, b) {
  const rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/**
 * V1's "Uncollected Nearby" (category-panel.js:1139-1211): up to 5
 * places in this category within 100km that YOU haven't collected,
 * nearest first, coordinate-deduped. Exported for tests.
 */
export function uncollectedNearby(rows, myLocationIds, here, limitKm = 100) {
  const seenCoord = new Set();
  const out = [];
  const byId = new Map();
  for (const r of rows) {
    const loc = r.locations;
    if (!loc?.id || loc.lat == null || loc.lng == null || byId.has(loc.id)) continue;
    byId.set(loc.id, loc);
  }
  for (const loc of byId.values()) {
    if (myLocationIds.has(loc.id)) continue;
    const coordKey = `${loc.lat.toFixed(4)},${loc.lng.toFixed(4)}`;
    if (seenCoord.has(coordKey)) continue;
    seenCoord.add(coordKey);
    const km = haversineKm(here, loc);
    if (km <= limitKm) out.push({ ...loc, km });
  }
  return out.sort((a, b) => a.km - b.km).slice(0, 5);
}

// Top Collectors now loads through the shared fetchCategoryLeaderboard
// (./leaderboard.js) — the log flow's post-save rank nudge reads the
// same cache, so a hop from this page to the Log form costs nothing.

/**
 * V1 calculateLeaderboard: distinct locations per user, descending.
 * No longer the production path — kept (with its tests) as the executable
 * spec that get_category_leaderboard mirrors.
 */
export function computeLeaderboard(rows) {
  const byUser = new Map();
  for (const row of rows) {
    if (!row.user_id) continue;
    const entry = byUser.get(row.user_id) || {
      userId: row.user_id,
      name: row.public_profiles?.full_name || row.public_profiles?.username || 'Explorer',
      username: row.public_profiles?.username || '',
      locations: new Set(),
    };
    if (row.location_id) entry.locations.add(row.location_id);
    byUser.set(row.user_id, entry);
  }
  return [...byUser.values()]
    .map((u) => ({ ...u, locationCount: u.locations.size }))
    .sort((a, b) => b.locationCount - a.locationCount);
}

/** V1's Set math: children the user has at least one yoink in. Exported for tests. */
export function computeProgress(children, userYoinkedCategoryIds) {
  const done = children.filter((c) => userYoinkedCategoryIds.has(c.id)).length;
  return { done, total: children.length };
}

/**
 * The Set checklist as a TREE (2026-07-12 feedback): a nested set like
 * Food Type has groups (Street Food, Desserts…) ABOVE the yoinkable
 * choices. Leaves are the choices (✓/○); a group aggregates its
 * subtree's leaf progress and expands in place. A flat set (National
 * Parks) is all leaves and renders exactly as before. Each level sorts
 * completed-first, then alphabetical. Exported for tests.
 */
export function buildSetTree(catalog, rootId, userYoinkedCategoryIds) {
  const byParent = new Map();
  for (const c of catalog) {
    const key = c.parent_id || null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(c);
  }
  const sortLevel = (nodes) => nodes.sort((a, b) =>
    (a.done !== b.done ? (a.done ? -1 : 1) : a.cat.name.localeCompare(b.cat.name)));
  const build = (cat) => {
    const kids = (byParent.get(cat.id) || []).map(build);
    if (!kids.length) {
      const done = userYoinkedCategoryIds.has(cat.id);
      return { cat, kids, leaves: 1, doneLeaves: done ? 1 : 0, done };
    }
    sortLevel(kids);
    const leaves = kids.reduce((n, k) => n + k.leaves, 0);
    const doneLeaves = kids.reduce((n, k) => n + k.doneLeaves, 0);
    return { cat, kids, leaves, doneLeaves, done: doneLeaves === leaves };
  };
  return sortLevel((byParent.get(rootId) || []).map(build));
}
